import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Base premium styles shared across all variants
const premiumBase = `
  inline-flex items-center justify-center gap-2 whitespace-nowrap 
  font-semibold transition-all duration-200 ease-out
  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
  disabled:pointer-events-none disabled:opacity-50
  [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0
  active:scale-[0.98]
`

const buttonVariants = cva(
  premiumBase,
  {
    variants: {
      variant: {
        // Existing variants - updated with subtle relief
        default: `
          bg-gradient-to-b from-primary to-primary/95 text-primary-foreground 
          border-b-2 border-primary/80
          shadow-md shadow-primary/20
          hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5
          active:shadow-sm
          focus-visible:ring-primary
          rounded-xl
        `,
        destructive: `
          bg-gradient-to-b from-destructive to-destructive/95 text-destructive-foreground 
          border-b-2 border-destructive/80
          shadow-md shadow-destructive/20
          hover:shadow-lg hover:shadow-destructive/30 hover:-translate-y-0.5
          active:shadow-sm
          focus-visible:ring-destructive
          rounded-xl
        `,
        outline: `
          border-2 border-input bg-background text-foreground 
          shadow-sm
          hover:bg-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-md
          active:shadow-sm
          rounded-xl
        `,
        secondary: `
          bg-gradient-to-b from-secondary to-secondary/95 text-secondary-foreground 
          border-b-2 border-secondary/80
          shadow-md shadow-secondary/20
          hover:shadow-lg hover:shadow-secondary/30 hover:-translate-y-0.5
          active:shadow-sm
          focus-visible:ring-secondary
          rounded-xl
        `,
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-xl",
        link: "text-primary underline-offset-4 hover:underline",
        
        // === NEW PREMIUM VARIANTS ===
        
        // Client (Orange) - "Décrire mon besoin", "Déposer mission"
        client: `
          bg-gradient-to-b from-btn-client to-btn-client-dark text-white 
          border-b-2 border-btn-client-dark
          shadow-md shadow-btn-client/25
          hover:shadow-lg hover:shadow-btn-client/35 hover:-translate-y-0.5
          active:shadow-sm
          focus-visible:ring-btn-client
          rounded-xl
        `,
        "client-outline": `
          border-2 border-btn-client bg-btn-client/10 text-btn-client
          backdrop-blur-sm
          shadow-sm
          hover:bg-btn-client/20 hover:-translate-y-0.5 hover:shadow-md
          active:shadow-sm
          focus-visible:ring-btn-client
          rounded-xl
        `,
        "client-dark": `
          border-2 border-[#F57C00] bg-[#F57C00]/15 text-white
          backdrop-blur-sm
          shadow-sm shadow-[#F57C00]/20
          hover:bg-[#F57C00]/25 hover:-translate-y-0.5 hover:shadow-md hover:shadow-[#F57C00]/30
          active:shadow-sm
          focus-visible:ring-[#F57C00]
          rounded-xl
        `,
        
        // Prestataire (Green) - "Devenir prestataire", "S'inscrire"
        prestataire: `
          bg-gradient-to-b from-btn-prestataire to-btn-prestataire-dark text-white 
          border-b-2 border-btn-prestataire-dark
          shadow-md shadow-btn-prestataire/25
          hover:shadow-lg hover:shadow-btn-prestataire/35 hover:-translate-y-0.5
          active:shadow-sm
          focus-visible:ring-btn-prestataire
          rounded-xl
        `,
        "prestataire-outline": `
          border-2 border-btn-client bg-btn-client/10 text-btn-client
          backdrop-blur-sm
          shadow-sm
          hover:bg-btn-client/20 hover:-translate-y-0.5 hover:shadow-md
          active:shadow-sm
          focus-visible:ring-btn-client
          rounded-xl
        `,
        "prestataire-dark": `
          border-2 border-[#2E9D5E] bg-[#2E9D5E]/15 text-white
          backdrop-blur-sm
          shadow-sm shadow-[#2E9D5E]/20
          hover:bg-[#2E9D5E]/25 hover:-translate-y-0.5 hover:shadow-md hover:shadow-[#2E9D5E]/30
          active:shadow-sm
          focus-visible:ring-[#2E9D5E]
          rounded-xl
        `,
        
        // Tarif (White/Black) - "Voir les tarifs"
        tarif: `
          bg-white text-btn-general border-2 border-border
          shadow-md
          hover:bg-btn-general hover:text-white hover:border-btn-general hover:-translate-y-0.5 hover:shadow-lg
          active:shadow-sm
          focus-visible:ring-btn-general
          rounded-xl
        `,
        "tarif-dark": `
          bg-white/90 text-btn-general border-2 border-white/30
          backdrop-blur-sm
          shadow-md
          hover:bg-white hover:-translate-y-0.5 hover:shadow-lg
          active:shadow-sm
          focus-visible:ring-white
          rounded-xl
        `,
        
        // General (Dark Blue) - "En savoir plus", "FAQ"
        general: `
          bg-gradient-to-b from-btn-general to-btn-general-dark text-white 
          border-b-2 border-btn-general-dark
          shadow-md shadow-btn-general/25
          hover:shadow-lg hover:shadow-btn-general/35 hover:-translate-y-0.5
          active:shadow-sm
          focus-visible:ring-btn-general
          rounded-xl
        `,
        "general-outline": `
          border-2 border-btn-general bg-btn-general/5 text-btn-general
          shadow-sm
          hover:bg-btn-general/10 hover:-translate-y-0.5 hover:shadow-md
          active:shadow-sm
          focus-visible:ring-btn-general
          rounded-xl
        `,
        "general-dark": `
          border-2 border-white/30 bg-white/10 text-white
          backdrop-blur-sm
          shadow-sm
          hover:bg-white/20 hover:-translate-y-0.5 hover:shadow-md
          active:shadow-sm
          focus-visible:ring-white
          rounded-xl
        `,
        
        // Legacy variants kept for compatibility
        "outline-light": `
          border-2 border-white/30 bg-white/10 text-white 
          backdrop-blur-sm
          shadow-sm
          hover:bg-white/20 hover:-translate-y-0.5 hover:shadow-md
          active:shadow-sm
          rounded-xl
        `,
        "ghost-light": "text-white hover:bg-white/10 hover:text-white rounded-xl",
        "primary-contrast": `
          bg-white text-primary 
          shadow-md
          hover:bg-white/90 hover:-translate-y-0.5 hover:shadow-lg
          active:shadow-sm
          rounded-xl
        `,
      },
      size: {
        default: "h-10 px-4 py-2 text-sm",
        sm: "h-9 px-3 py-1.5 text-sm",
        lg: "h-12 px-6 py-3 text-base",
        icon: "h-10 w-10",
        // Premium sizes
        premium: "min-h-[56px] px-8 py-4 text-base",
        "premium-sm": "min-h-[48px] px-6 py-3 text-sm",
        "premium-lg": "min-h-[60px] px-10 py-4 text-lg",
        // Mobile sizes
        "mobile-full": "h-10 px-4 py-2 w-full sm:w-auto text-sm",
        "mobile-compact": "h-8 px-2 py-1 text-xs",
        tile: "h-auto min-h-[3.5rem] px-3 py-2 text-sm font-medium",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
